// Utility functions used by report components
import React from 'react';
import {
  Radio,
  Tooltip,
  stringToDate,
} from '@makeably/creativex-design-system';
import {
  arrayIf,
  findObjectByValue,
  findObjectsByValue,
  objectValues,
  sortObjectArray,
} from 'utilities/array';
import {
  NULL_DIMENSION,
} from 'utilities/item';
import { valueIf } from 'utilities/object';
import { titleize } from 'utilities/string';

export const FILTER_KEY = 'filter';
export const NONE_KEY = 'none';

export const noneHeader = {
  key: NONE_KEY,
  label: 'No Segments\nSelected',
  sortable: false,
};

export const indexHeader = {
  key: 'index',
  label: '#',
  sortable: false,
};

export function updateVizMetric(metric, selected) {
  if (metric && findObjectByValue(selected, metric)) {
    return metric;
  }
  return selected[0];
}

export function updateChartMetrics(metrics, selected) {
  if (metrics && metrics.length > 0) {
    const newChartMetrics = metrics.filter((metric) => findObjectByValue(selected, metric.value));

    if (newChartMetrics.length === 0 && selected.length > 0) {
      return [selected[0]];
    }

    return newChartMetrics;
  }
  return [];
}

function getMetricHeaders(metrics, vizMetrics, setVizMetric) {
  return metrics.map((metric) => {
    const {
      label,
      tooltip,
      value,
    } = metric;
    const isSelected = setVizMetric && Boolean(findObjectByValue(vizMetrics, value));

    let element = (
      <Tooltip label={tooltip}>{ label }</Tooltip>
    );

    if (setVizMetric) {
      element = (
        <div className="u-flexRow u-alignCenter">
          <Radio
            ariaLabel={`Select ${label}`}
            checked={isSelected}
            name="vizMetric"
            value={label}
            onChange={() => setVizMetric(metric)}
          />
          <Tooltip label={tooltip}>{ label }</Tooltip>
        </div>
      );
    }

    return {
      element,
      highlighted: isSelected,
      key: value,
      label,
    };
  });
}

export function getHeaders(segments, metrics, vizMetrics, setVizMetric = undefined) {
  const useNone = (segments.length === 0) && (metrics.length > 0);
  const useIndex = segments.length > 1;
  const segmentHeaders = segments.map(({ label, value }) => ({
    key: value,
    label,
  }));
  const metricHeaders = getMetricHeaders(metrics, vizMetrics, setVizMetric);

  return [
    ...arrayIf(useNone, noneHeader),
    ...arrayIf(useIndex, indexHeader),
    ...segmentHeaders,
    ...metricHeaders,
  ];
}

const metricTooltips = {
  adoptionRate: 'Percentage of creatives that have met an individual guideline.',
  averageScore: 'The average of your organization’s scores.',
  averageThirdPartyScore: 'The average of your organization’s third party scores.',
  lowQualitySpend: "Total spend behind assets that do not meet the applicable score's highest tier.",
  qualitySpend: "Total spend behind assets that meet the applicable score's highest tier.",
  qualitySpendRate: "The percentage of the spend behind assets meeting the score's highest tier threshold out of the total ad spend. All updated versions of a published asset are factored in.",
  scoreRate: "The percentage of assets meeting the score's highest tier threshold. All updated versions of a published asset are factored in.",
  totalAssets: 'All assets in CreativeX that are uploaded via Pre-flight or captured from an ad platform for In-Flight.',
  totalPosts: 'Total post count from assets captured by CreativeX.',
  totalSpend: 'Total spend from assets captured by CreativeX.',
};

const metricTypes = {
  adaptationRate: 'percent',
  averageScore: 'percent',
  lowQualitySpend: 'spend',
  qualitySpend: 'spend',
  qualitySpendRate: 'percent',
  scoreRate: 'percent',
  totalAssets: 'count',
  totalPosts: 'count',
  totalSpend: 'spend',
};

export const thirdPartyScoreSegmentTypes = ['string', 'boolean'];

export function getScoreMetric(score, key, format = 'percent') {
  return {
    format,
    label: score.metricLabels?.[key] ?? key,
    tooltip: metricTooltips[key],
    type: metricTypes[key],
    value: `${key}::${score.versionId}`,
  };
}

function getScoreMetrics(scores, canViewSpend) {
  return scores.reduce((arr, score) => (
    [
      ...arr,
      getScoreMetric(score, 'averageScore'),
      getScoreMetric(score, 'scoreRate'),
      ...arrayIf(
        canViewSpend, ...[
          getScoreMetric(score, 'qualitySpend', 'spend'),
          getScoreMetric(score, 'lowQualitySpend', 'spend'),
          getScoreMetric(score, 'qualitySpendRate'),
        ],
      ),
    ]
  ), [
    {
      group: true,
      label: 'Scores',
      value: 'scores',
    },
  ]);
}

function getThirdPartyScoreMetrics(thirdPartyScores) {
  if (!thirdPartyScores || thirdPartyScores.length === 0) return [];

  const scores = thirdPartyScores.reduce((arr, {
    id, name, valueType,
  }) => {
    if (!thirdPartyScoreSegmentTypes.includes(valueType)) {
      return [
        ...arr,
        {
          format: 'percent',
          label: `Average ${name}`,
          tooltip: metricTooltips.averageThirdPartyScore,
          type: metricTypes.averageScore,
          value: `averageThirdPartyScore::${id}`,
        },
      ];
    }
    return arr;
  }, []);

  return [
    {
      group: true,
      label: 'Third Party Scores',
      value: 'thirdPartyScores',
    },
    ...sortObjectArray(scores, 'label'),
  ];
}

// NOTE: used for Score and Time Period reports. format value is for chart axis tick formatting
export function getMetrics(canViewSpend, scores, thirdPartyScores) {
  const commonMetrics = [
    {
      format: 'number',
      label: 'Total\nPosts',
      tooltip: metricTooltips.totalPosts,
      type: metricTypes.totalPosts,
      value: 'totalPosts',
    },
    {
      format: 'number',
      label: 'Total\nAssets',
      tooltip: metricTooltips.totalAssets,
      type: metricTypes.totalAssets,
      value: 'totalAssets',
    },
  ];

  const spendMetric = {
    format: 'spend',
    label: 'Total\nSpend',
    tooltip: metricTooltips.totalSpend,
    type: metricTypes.totalSpend,
    value: 'totalSpend',
  };

  return [
    {
      group: true,
      label: 'General',
      value: 'general',
    },
    ...arrayIf(canViewSpend, spendMetric),
    ...commonMetrics,
    ...getScoreMetrics(scores, canViewSpend),
    ...getThirdPartyScoreMetrics(thirdPartyScores),
  ];
}

export function getGuidelineMetrics(canViewSpend) {
  const spendMetric = {
    label: 'Total\nSpend',
    tooltip: metricTooltips.totalSpend,
    type: metricTypes.totalSpend,
    value: 'totalSpend',
  };

  return [
    ...arrayIf(canViewSpend, spendMetric),
    {
      label: 'Total\nPosts',
      tooltip: metricTooltips.totalPosts,
      type: metricTypes.totalPosts,
      value: 'totalPosts',
    },
    {
      label: 'Total\nAssets',
      tooltip: metricTooltips.totalAssets,
      type: metricTypes.totalAssets,
      value: 'totalAssets',
    },
    {
      label: 'Adoption\nRate',
      tooltip: metricTooltips.adoptionRate,
      type: metricTypes.adaptationRate,
      value: 'adoptionRate',
    }];
}

function getScoreSegments(scores) {
  const segments = scores.map(({ name, versionId }) => ({
    label: `${name}: Tiers`,
    value: `rank::${versionId}`,
  }));

  return [
    {
      group: true,
      label: 'Scoring Tiers',
      value: 'scoringTiers',
    },
    ...sortObjectArray(segments, 'label'),
  ];
}

function getCustomSegments(names) {
  if (!names || names.length === 0) return [];

  const segments = names.map((name) => ({
    label: name,
    value: `customSegment::${name}`,
  }));

  return [
    {
      group: true,
      label: 'Custom Segments',
      value: 'customSegments',
    },
    ...sortObjectArray(segments, 'label'),
  ];
}

function getThirdPartyScoreSegments(thirdPartyScores) {
  if (!thirdPartyScores || thirdPartyScores.length === 0) return [];

  const segments = thirdPartyScores.reduce((arr, {
    id,
    name,
    valueType,
  }) => {
    if (thirdPartyScoreSegmentTypes.includes(valueType)) {
      return [...arr, {
        label: name,
        value: `thirdPartyScore::${id}`,
      }];
    }
    return arr;
  }, []);

  if (segments.length === 0) return [];

  return [
    {
      group: true,
      label: 'Third Party Scores',
      value: 'thirdPartyScores',
    },
    ...sortObjectArray(segments, 'label'),
  ];
}

function getCustomFilterSegments(customFilters) {
  const customFilterSegmentValues = new Set();

  const segments = customFilters.reduce((arr, { id, name }) => {
    const value = `customFilter::${id}`;

    if (!customFilterSegmentValues.has(value)) {
      customFilterSegmentValues.add(value);
      arr.push({
        label: name,
        value,
      });
    }

    return arr;
  }, []);

  if (segments.length === 0) return [];

  return [
    {
      group: true,
      label: 'Custom Filters',
      value: 'customFilters',
    },
    ...sortObjectArray(segments, 'label'),
  ];
}

function getCommonSegments(additionalSegments) {
  const segments = [
    {
      label: 'Ad\nFormat',
      value: 'adFormat',
    },
    {
      label: 'Asset\nType',
      value: 'assetType',
    },
    {
      label: 'Brand',
      value: 'brand',
    },
    {
      label: 'Campaign Status',
      value: 'campaignStatus',
    },
    {
      label: 'Channel',
      value: 'channel',
    },
    {
      label: 'Content Type',
      value: 'contentType',
    },
    {
      label: 'Market',
      value: 'market',
    },
  ];
  if (additionalSegments) {
    segments.push(...additionalSegments);
    segments.sort((a, b) => a.label.localeCompare(b.label));
  }
  return [
    {
      group: true,
      label: 'General',
      value: 'general',
    },
    ...segments,
  ];
}

export function getScoresSegments(scores, customFilters, customSegments, thirdPartyScores) {
  return [
    ...getCommonSegments([{
      label: 'Creative Agency',
      value: 'creativeAgency',
    },
    {
      label: 'Media Agency',
      value: 'mediaAgency',
    },
    {
      label: 'Partner',
      value: 'partner',
    },
    {
      label: 'Pre-Flight Submitter',
      value: 'submitter',
    },
    {
      label: 'Publisher',
      value: 'publisher',
    }]),
    ...getScoreSegments(scores),
    ...getCustomFilterSegments(customFilters),
    ...getCustomSegments(customSegments),
    ...getThirdPartyScoreSegments(thirdPartyScores),
  ];
}

export function getGuidelinesSegments(scores, customFilters, customSegments) {
  return [
    ...getCommonSegments([{
      disabled: true,
      label: 'Guideline',
      value: 'guideline',
    }, {
      label: 'Score',
      value: 'scoreName',
    }]),
    ...getScoreSegments(scores),
    ...getCustomFilterSegments(customFilters),
    ...getCustomSegments(customSegments),
  ];
}

function getLabeledRanks(record, labeledScores) {
  return labeledScores.reduce((obj, { labelsByRank, versionId }) => {
    const key = `rank::${versionId}`;
    const value = record[key];
    const label = labelsByRank[value];

    return {
      ...obj,
      [key]: label,
    };
  }, {});
}

function getCustomFilterOptions(record, customFilters, attributeLookups) {
  return customFilters.reduce((obj, filterOption) => {
    const {
      dimension,
      id,
      optionName,
      optionValues,
    } = filterOption;

    let recordDimension = record[dimension];
    // NOTE: Handle attributes that are not yet updated to their labels
    if (['adFormat', 'channel'].includes(dimension)) {
      const dimensionKey = `${dimension}Key`;
      recordDimension = attributeLookups[dimension]?.[record[dimensionKey]];
    } else if (dimension === 'brand') {
      recordDimension = attributeLookups.brand?.[record.brandId];
    }

    if (optionValues.includes(recordDimension)) {
      const key = `customFilter::${id}`;
      const value = [...(obj[key] || []), optionName];

      return {
        ...obj,
        [key]: value,
      };
    }

    return obj;
  }, {});
}

function getAttributeLookups(record, attributeLookups) {
  return {
    adFormat: attributeLookups.adFormat?.[record.adFormatKey] || 'N/A',
    brand: attributeLookups.brand?.[record.brandId] || 'N/A',
    channel: attributeLookups.channel?.[record.channelKey] || 'N/A',
  };
}

function getLabeledDates(record, dateLabels) {
  if (!dateLabels) return {};

  const {
    key,
    labels,
  } = dateLabels;
  const value = record.dateBucket;

  return {
    [key]: labels[value],
  };
}

function formatThirdPartyScore(value, type) {
  if (type === 'boolean') {
    return titleize(value) || 'N/A';
  } else if (type === 'float') {
    return value === null ? null : parseFloat(value);
  }
  return value;
}

function getFormattedThirdPartyScores(record, thirdPartyScores) {
  if (!thirdPartyScores || thirdPartyScores.length === 0) return {};

  return thirdPartyScores.reduce((obj, { id, valueType }) => {
    const key = `thirdPartyScore::${id}`;
    const value = record[key];
    const formattedValue = formatThirdPartyScore(value, valueType);

    return {
      ...obj,
      [key]: formattedValue,
    };
  }, {});
}

export function preprocessRecords(
  rawRecords,
  scores,
  customFilters,
  attributeLookups,
  thirdPartyScores,
  dateLabels,
) {
  const labeledScores = scores.map(({ versionId, tiers }) => ({
    labelsByRank: tiers.reduce((obj, { label, rank }) => ({
      ...obj,
      [rank]: label,
    }), {}),
    versionId,
  }));

  return rawRecords.map((record) => ({
    ...record,
    ...getLabeledRanks(record, labeledScores),
    ...getCustomFilterOptions(record, customFilters, attributeLookups),
    ...getAttributeLookups(record, attributeLookups),
    ...getLabeledDates(record, dateLabels),
    ...getFormattedThirdPartyScores(record, thirdPartyScores),
  }));
}

export function calcPropertiesJson({
  selectedColCount,
  selectedChartMetrics,
  selectedDateOption,
  selectedFilters,
  selectedLineCount,
  selectedMetrics,
  selectedSegments,
  selectedTimePeriod,
  selectedVizType,
  showTotals,
  sort,
  vizMetric,
}) {
  const chartMetricsObj = {};
  if (selectedChartMetrics?.length > 0 || vizMetric) {
    chartMetricsObj.chartMetrics = objectValues(selectedChartMetrics ?? [vizMetric]);
  }

  const properties = {
    ...valueIf(selectedColCount, 'colCount', selectedColCount?.value),
    ...chartMetricsObj,
    endDate: selectedDateOption?.endDate,
    filters: selectedFilters,
    metrics: objectValues(selectedMetrics),
    segments: objectValues(selectedSegments),
    ...valueIf(sort, 'sort', sort),
    startDate: selectedDateOption?.startDate,
    ...valueIf(selectedTimePeriod, 'timePeriod', selectedTimePeriod?.value),
    ...valueIf(selectedDateOption?.type, 'type', selectedDateOption?.type),
    ...valueIf(selectedLineCount, 'lineCount', selectedLineCount?.value),
    ...valueIf(selectedVizType, 'vizType', selectedVizType?.value),
    ...valueIf(showTotals !== undefined, 'showTotals', showTotals),
  };

  return JSON.stringify(properties);
}

function findDateOption(options, startDate, endDate) {
  if (!options) return null;

  const found = options.find((opt) => opt.startDate === startDate && opt.endDate === endDate);

  return found ?? options[0];
}

function findDateRange(customRangeProps, properties) {
  if (!customRangeProps) return null;

  const {
    endDate: maxDate,
    startDate: minDate,
  } = customRangeProps;

  const {
    endDate: savedEndDate,
    startDate: savedStartDate,
  } = properties;

  const withinMax = stringToDate(maxDate).getTime() >= stringToDate(savedEndDate).getTime();
  const withinMin = stringToDate(minDate).getTime() <= stringToDate(savedStartDate).getTime();

  return {
    endDate: withinMax ? savedEndDate : maxDate,
    startDate: withinMin ? savedStartDate : minDate,
    type: 'dynamic',
  };
}

export function parseProperties(properties, {
  columnCounts,
  customRangeProps,
  dateOptions,
  lineCounts,
  metrics,
  segments,
  timePeriods,
  visualizationTypes,
}) {
  return {
    ...valueIf(columnCounts, 'selectedColCount', findObjectByValue(columnCounts, properties.colCount ?? columnCounts?.[0]?.value)),
    selectedChartMetrics: findObjectsByValue(metrics, properties.chartMetrics ?? []),
    ...valueIf(dateOptions, 'selectedDateOption', findDateOption(dateOptions, properties.startDate, properties.endDate)),
    selectedDateRange: findDateRange(customRangeProps, properties),
    selectedFilters: properties.filters ?? {},
    ...valueIf(lineCounts, 'selectedLineCount', findObjectByValue(lineCounts, properties.lineCount ?? lineCounts?.[0]?.value)),
    selectedMetrics: findObjectsByValue(metrics, properties.metrics),
    selectedSegments: findObjectsByValue(segments, properties.segments),
    ...valueIf(timePeriods, 'selectedTimePeriod', findObjectByValue(timePeriods, properties.timePeriod)),
    ...valueIf(visualizationTypes, 'selectedVizType', findObjectByValue(visualizationTypes, properties.vizType ?? visualizationTypes?.[0]?.value)),
    showTotals: properties.showTotals ?? false,
    sort: properties.sort,
    useCustomDates: properties?.type === 'dynamic' && customRangeProps.customDatesEnabled,
    vizMetric: findObjectByValue(metrics, properties.chartMetrics?.[0]) ?? metrics[0],
  };
}

// NOTE: Use this to avoid passing null values which frequently cause prop type validation errors
export function nullableMetric(value, labelCallback = undefined) {
  if (value === null) return NULL_DIMENSION;

  return {
    label: labelCallback?.(value) ?? value,
    value,
  };
}

// NOTE: Use this to label zero metric values as 'N/A'
export function zeroableMetric(value, labelCallback = undefined) {
  if (value === 0) return NULL_DIMENSION;

  return {
    label: labelCallback?.(value) ?? value,
    value,
  };
}

export function setTableStyles(selectedSegments) {
  if (selectedSegments?.length === 0) return;

  const sheet = new CSSStyleSheet();
  const numSegments = selectedSegments.length;
  const lastSegment = selectedSegments[numSegments - 1];

  // NOTE: Adding 1 when the row index column is included
  const lastSegmentColumnIndex = numSegments > 1 ? numSegments + 1 : numSegments;

  const tableBorderStyle = 'border-right: 1px solid var(--grey-200)';

  sheet.replaceSync(`
    tr th:nth-child(${lastSegmentColumnIndex}) { ${tableBorderStyle} }
    tr .itemsTableColumn-${lastSegment.value} { ${tableBorderStyle} }
  `);
  document.adoptedStyleSheets = [sheet];
}
